/**
 * useCommandExecutor - コマンドを実行するためのフック
 *
 * TabCreationComboBoxやその他のコンポーネントから使用して、
 * AIエージェント向けコマンドを実行する
 */

import { useCallback, useEffect, useRef } from 'react';
import { useProjectData } from '../stores/hooks/useProjectData';
import { usePaneLayout } from '../stores/hooks/usePaneLayout';
import { useReports } from './useReports';
import { findActiveDatasetIdForTab } from '../utils/activeDatasetUtils';
import {
  isCommandMode,
  parseCommand,
  executeCommand,
  initializeCommandSystem,
  type CommandResult,
  type CommandContext
} from '../command-system';
import type { TabState, PaneNode, TabContainer } from '../../types';
import type { ReportElementUnion } from '../../types/report.types';

/**
 * PaneNodeを再帰的に走査して全タブ情報を収集
 */
function collectAllTabs(node: PaneNode): Array<{ tab: TabState; containerId: string }> {
  if (node.type === 'container') {
    return (node as TabContainer).tabs.map(tab => ({ tab, containerId: node.id }));
  }
  // SplitPaneの場合は子を再帰的に走査
  return [
    ...collectAllTabs(node.leftChild),
    ...collectAllTabs(node.rightChild)
  ];
}

interface UseCommandExecutorResult {
  /** 入力がコマンドモードかどうかを判定 */
  isCommandMode: (input: string) => boolean;
  /** コマンドを実行 */
  execute: (input: string) => Promise<CommandResult>;
  /** 実行中かどうか */
  isExecuting: boolean;
}

/**
 * コマンドを実行するためのフック
 */
export function useCommandExecutor(): UseCommandExecutorResult {
  const { project, datasets, addDerivedDataset, updateProject } = useProjectData();
  const { layout, addTab, findTab, updateTabState } = usePaneLayout();
  const isExecutingRef = useRef(false);

  // レポート管理フック
  // updateProjectはupdater関数を受け取るが、useReportsは更新後のprojectを受け取る
  const handleProjectUpdate = useCallback((updatedProject: import('../../types').MIDASProject) => {
    updateProject(() => updatedProject);
  }, [updateProject]);

  const { createReport, addElement } = useReports({
    project,
    onProjectUpdate: handleProjectUpdate
  });

  // 初期化
  useEffect(() => {
    initializeCommandSystem();
  }, []);

  // コマンド実行
  const execute = useCallback(async (input: string): Promise<CommandResult> => {
    if (isExecutingRef.current) {
      return {
        success: false,
        message: 'Another command is currently executing',
        error: {
          code: 'BUSY',
          message: 'Wait for the current command to complete'
        }
      };
    }

    // パース
    const parseResult = parseCommand(input);
    if (!parseResult.success) {
      return {
        success: false,
        message: parseResult.error,
        error: {
          code: 'PARSE_ERROR',
          message: parseResult.error
        }
      };
    }

    // コンテキストを構築
    const context: CommandContext = {
      project,
      datasets,
      addTab: (tab: TabState) => {
        const containerId = layout.activeContainerId;
        if (containerId) {
          addTab(containerId, tab);
        }
      },
      updateTabViewState: (tabId: string, tabViewState: Record<string, unknown>): boolean => {
        const tabInfo = findTab(tabId);
        if (!tabInfo) {
          return false;
        }
        updateTabState(tabInfo.container.id, tabId, (tab) => ({
          ...tab,
          tabViewState: {
            ...tab.tabViewState,
            ...tabViewState
          }
        }));
        return true;
      },
      addDerivedDataset,
      addReportElement: async (element: ReportElementUnion, options?: { reportId?: string; newReportName?: string }): Promise<string | null> => {
        // 既存レポートが指定されている場合はそこに追加
        if (options?.reportId) {
          const success = await addElement(options.reportId, element);
          return success ? options.reportId : null;
        }

        // 新規レポートを作成
        const name = options?.newReportName ?? `${element.type.replace(/_/g, ' ')} Report`;
        const report = await createReport(name);
        if (!report) {
          return null;
        }
        await addElement(report.id, element);
        return report.id;
      },
      getActiveContainerId: () => layout.activeContainerId ?? null,
      getActiveDatasetId: () => findActiveDatasetIdForTab(layout, ''),
      getTabs: () => {
        const allTabs = collectAllTabs(layout.rootPane);
        return allTabs.map(({ tab, containerId }) => ({
          id: tab.id,
          type: tab.type,
          title: tab.title,
          containerId
        }));
      },
      getModels: () => Object.values(project?.models ?? {}),
      getReports: () => project?.reports ?? []
    };

    // 実行
    isExecutingRef.current = true;
    try {
      const result = await executeCommand(parseResult.command, context);
      return result;
    } finally {
      isExecutingRef.current = false;
    }
  }, [project, datasets, layout, addTab, findTab, updateTabState, addDerivedDataset, createReport, addElement]);

  return {
    isCommandMode,
    execute,
    isExecuting: isExecutingRef.current
  };
}
